import { getEnv } from '../utils/env';
import { createPublicAuthApi } from '@waedo/frontend-core/auth';

export const AUTH_STORAGE_KEY = 'dohockey.auth.session';
export const AUTH_USER_STORAGE_KEY = 'dohockey.auth.user';

type JsonRecord = Record<string, unknown>;

export class AuthApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
    this.name = 'AuthApiError';
  }
}

export interface AuthSession {
  access_token?: string;
  refresh_token?: string;
  cookie_authenticated?: boolean;
  token_type?: string;
  expires_in?: number;
  expires_at?: number;
}

export interface AuthApiResponse {
  revision?: string;
  ok?: boolean;
  session: AuthSession;
  user?: JsonRecord | null;
  error?: string;
  code?: string;
  retry_after_seconds?: number;
}

export interface StoredAuthUser {
  id?: string;
  email?: string;
  role?: string;
  club_id?: string;
}

const asRecord = (value: unknown): JsonRecord | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : null;

const readString = (source: JsonRecord, keys: string[]): string => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return '';
};

const readNumber = (source: JsonRecord, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
};

const normalizeAuthPayload = (payload: unknown): AuthApiResponse => {
  const top = asRecord(payload) || {};
  const nestedData = asRecord(top.data) || {};
  const source = ('session' in top || 'user' in top) ? top : nestedData;
  const nestedSession = asRecord(source.session) || {};

  const accessToken =
    readString(nestedSession, ['access_token', 'accessToken']) ||
    readString(source, ['access_token', 'accessToken']);
  const refreshToken =
    readString(nestedSession, ['refresh_token', 'refreshToken']) ||
    readString(source, ['refresh_token', 'refreshToken']);

  return {
    ok: typeof top.ok === 'boolean' ? top.ok : undefined,
    session: {
      access_token: accessToken,
      refresh_token: refreshToken,
      cookie_authenticated: nestedSession.cookie_authenticated === true,
      token_type: readString(nestedSession, ['token_type', 'tokenType']) || undefined,
      expires_in: readNumber(nestedSession, ['expires_in', 'expiresIn']),
      expires_at: readNumber(nestedSession, ['expires_at', 'expiresAt']),
    },
    user: asRecord(source.user) || asRecord(nestedData.user),
    error: readString(top, ['error']) || undefined,
    code: readString(top, ['code']) || undefined,
  };
};

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const getApiKey = (): string => getEnv('VITE_AI_API_KEY', '');
const publicAuthApi = createPublicAuthApi({
  getApiBaseUrl: toApiBaseUrl,
  getApiKey,
});
let sessionRefreshPromise: Promise<AuthApiResponse> | null = null;
let sessionValidationPromise: Promise<void> | null = null;
let authGeneration = 0;
const SESSION_REQUEST_TIMEOUT_MS = 15_000;

const sessionSnapshot = (): string | null => {
  try { return typeof window === 'undefined' ? null : window.localStorage.getItem(AUTH_STORAGE_KEY); }
  catch { return null; }
};
export const isInvalidAuthSessionError = (error: unknown): error is AuthApiError =>
  error instanceof AuthApiError && error.status === 401 && error.code !== 'API_KEY_INVALID';

export const getCsrfToken = (): string => {
  if (typeof document === 'undefined') return '';
  const prefix = 'dohockey_csrf=';
  const entry = document.cookie.split(';').map((value) => value.trim()).find((value) => value.startsWith(prefix));
  return entry ? decodeURIComponent(entry.slice(prefix.length)) : '';
};

const buildHeaders = (token?: string): HeadersInit => ({
  'Content-Type': 'application/json',
  'x-api-key': getApiKey(),
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  ...(getCsrfToken() ? { 'X-CSRF-Token': getCsrfToken() } : {}),
});

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const requestAuthEndpoint = async (
  path: string,
  options: RequestInit = {},
  token?: string
): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SESSION_REQUEST_TIMEOUT_MS);
  let response: Response;
  let payload: unknown;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...options,
      signal: options.signal || controller.signal,
      credentials: 'include',
      headers: { ...buildHeaders(token), ...(options.headers || {}) },
    });
    payload = await parseJson(response);
    controller.signal.throwIfAborted();
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string'
        ? payload.message
        : `Auth API request failed (${response.status})`;
    const record = asRecord(payload);
    const code = record?.detail === 'Invalid API key' ? 'API_KEY_INVALID' : undefined;
    throw new AuthApiError(message, response.status, code);
  }

  return payload;
};

export const loginWithPassword = async (email: string, password: string): Promise<AuthApiResponse> => {
  return publicAuthApi.loginWithPassword(email, password);
};

export const requestOtpLogin = async (email: string, redirectTo?: string): Promise<void> => {
  await publicAuthApi.requestMagicLink(email, redirectTo);
};

export const verifyOtpLogin = async (email: string, token: string): Promise<AuthApiResponse> => {
  return publicAuthApi.verifyMagicLink(email, token);
};

export const requestPasswordReset = publicAuthApi.requestPasswordReset;
export const verifyPasswordReset = publicAuthApi.verifyPasswordReset;

const requestSessionRefresh = async (refreshToken?: string): Promise<AuthApiResponse> => {
  const payload = await requestAuthEndpoint('/api/v1/auth/refresh', {
    method: 'POST',
    ...(refreshToken ? { body: JSON.stringify({ refresh_token: refreshToken }) } : {}),
  });
  const normalized = normalizeAuthPayload(payload);
  if (normalized.ok === false) {
    throw new AuthApiError(
      normalized.error || 'Authentication session refresh failed',
      normalized.code === 'AUTH_INVALID_SESSION' ? 401 : 503,
    );
  }
  if (!normalized.session.cookie_authenticated && !normalized.session.access_token) {
    throw new AuthApiError('Authentication refresh returned no session', 503);
  }
  return normalized;
};

export const getCurrentAuthSession = async (accessToken?: string): Promise<AuthApiResponse> => {
  const payload = await requestAuthEndpoint('/api/v1/auth/session', {
    method: 'GET',
  }, accessToken);
  const normalized = normalizeAuthPayload(payload);
  if (normalized.ok === false || !normalized.user?.id) {
    throw new AuthApiError('Invalid authentication response', 503);
  }
  if (!normalized.session.access_token) {
    normalized.session.cookie_authenticated = true;
  }
  return normalized;
};

export const logoutAuthSession = async (accessToken?: string): Promise<void> => {
  await requestAuthEndpoint('/api/v1/auth/logout', { method: 'POST' }, accessToken);
};

export const changePasswordWithCurrentPassword = async (
  currentPassword: string,
  newPassword: string,
  accessToken?: string
): Promise<void> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) throw new Error('VITE_AI_API_URL is not configured');
  const response = await authenticatedFetch(`${baseUrl}/api/v1/auth/password/change`, {
    method: 'POST',
    headers: buildHeaders(accessToken),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!response.ok) throw new AuthApiError(`Password change failed (${response.status})`, response.status);
};

export const getAuthHeaders = (extraHeaders: HeadersInit = {}): HeadersInit => {
  const headers = new Headers(buildHeaders(getStoredAccessToken() || undefined));
  // Set entries individually: Headers(record) combines differently cased keys,
  // producing an invalid Content-Type such as "application/json, application/json".
  const entries = extraHeaders instanceof Headers ? extraHeaders.entries()
    : Array.isArray(extraHeaders) ? extraHeaders : Object.entries(extraHeaders);
  for (const [key, value] of entries) headers.set(key, value);
  // These values belong to the current session, never a caller's old snapshot.
  const token = getStoredAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  else headers.delete('Authorization');
  const csrf = getCsrfToken();
  if (csrf) headers.set('X-CSRF-Token', csrf);
  else headers.delete('X-CSRF-Token');
  return Object.fromEntries(headers.entries());
};

const notifyInvalidSession = (): void => {
  clearStoredAuthSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-session-invalid'));
  }
};

/** Share renewal across initialization, profile requests and streams, and serialize tabs. */
export const refreshAuthSession = async (refreshToken?: string): Promise<AuthApiResponse> => {
  if (sessionRefreshPromise) return sessionRefreshPromise;
  const snapshot = sessionSnapshot();
  const generation = authGeneration;
  const stillCurrent = () => generation === authGeneration && sessionSnapshot() === snapshot;
  const renew = async (): Promise<AuthApiResponse> => {
    if (generation !== authGeneration) throw new DOMException('Session changed', 'AbortError');
    const stored = getStoredAuthSession();
    if (sessionSnapshot() !== snapshot) {
      if (stored) return stored; // Another tab already renewed while we waited for its lock.
      throw new DOMException('Session cleared', 'AbortError');
    }
    try {
      const renewed = await requestSessionRefresh(stored?.session.refresh_token || refreshToken);
      if (!stillCurrent()) throw new DOMException('Session changed', 'AbortError');
      saveAuthSession(renewed);
      return renewed;
    } catch (error) {
      if (!isInvalidAuthSessionError(error) || !stillCurrent()) throw error;
      // A different origin or an older tab may have rotated the shared HttpOnly
      // cookie without participating in our Web Lock. Check the winning session.
      try {
        const current = await getCurrentAuthSession();
        if (!stillCurrent()) throw new DOMException('Session changed', 'AbortError');
        saveAuthSession(current);
        return current;
      } catch (validationError) {
        if (isInvalidAuthSessionError(validationError) && stillCurrent()) notifyInvalidSession();
        throw validationError;
      }
    }
  };
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  const run = async () => locks ? await locks.request('dohockey:session-refresh', renew) : await renew();
  sessionRefreshPromise = run().finally(() => { sessionRefreshPromise = null; });
  return sessionRefreshPromise;
};

/** Streams cannot inspect a 401 from EventSource; validate before (re)connecting. */
export const ensureAuthSession = async (): Promise<void> => {
  if (sessionValidationPromise) return sessionValidationPromise;
  const validate = async () => {
    if (sessionRefreshPromise) { await sessionRefreshPromise; return; }
    const snapshot = sessionSnapshot();
    const stored = getStoredAuthSession();
    if (!stored) return;
    if (stored.session.expires_at && stored.session.expires_at <= Date.now() / 1000 + 30) {
      await refreshAuthSession();
      return;
    }
    try {
      await getCurrentAuthSession(stored.session.access_token);
    } catch (error) {
      if (!isInvalidAuthSessionError(error)) throw error;
      if (sessionSnapshot() === snapshot) await refreshAuthSession();
    }
  };
  sessionValidationPromise = validate().finally(() => { sessionValidationPromise = null; });
  return sessionValidationPromise;
};

export const authenticatedFetch = async (
  input: string | URL,
  options: RequestInit = {}
): Promise<Response> => {
  const snapshot = sessionSnapshot();
  const generation = authGeneration;
  const userId = getStoredAuthSession()?.user?.id;
  const execute = () => {
    options.signal?.throwIfAborted();
    const headers = new Headers(getAuthHeaders(options.headers || {}));
    if (options.body instanceof FormData) headers.delete('Content-Type');
    return fetch(input, { ...options, credentials: 'include', headers });
  };

  const response = await execute();
  // Anonymous/public consumers must not initiate renewal or broadcast logout.
  if (response.status !== 401 || !snapshot) return response;
  options.signal?.throwIfAborted();
  if (sessionSnapshot() === snapshot) await refreshAuthSession();
  options.signal?.throwIfAborted();
  if (generation !== authGeneration || !hasStoredAuthSession()
      || (userId != null && getStoredAuthSession()?.user?.id !== userId)) {
    throw new DOMException('Session changed', 'AbortError');
  }
  // A late 401 may belong to a request sent before another request renewed.
  // Retry only the explicit auth rejection, never uncertain writes or 403s.
  return execute();
};

export const saveAuthSession = (payload: AuthApiResponse): void => {
  if (typeof window === 'undefined') return;
  try {
    const sanitized: AuthApiResponse = {
      ...payload,
      revision: `${Date.now()}:${Math.random()}`,
      session: {
        cookie_authenticated: true,
        token_type: 'cookie',
        expires_in: payload.session?.expires_in,
        expires_at: payload.session?.expires_at,
      },
    };
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(sanitized));
  } catch {
    // Ignore storage issues
  }
};

export const getStoredAuthSession = (): AuthApiResponse | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = normalizeAuthPayload(JSON.parse(raw));
    if (!parsed.session.cookie_authenticated && !parsed.session.access_token) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const getStoredAccessToken = (): string | null => {
  const stored = getStoredAuthSession();
  const token = stored?.session?.access_token;
  return typeof token === 'string' && token.trim().length > 0 ? token : null;
};

export const hasStoredAuthSession = (): boolean => {
  const session = getStoredAuthSession()?.session;
  return Boolean(session?.cookie_authenticated || session?.access_token);
};

export const clearStoredAuthSession = (): void => {
  authGeneration += 1;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Ignore storage issues
  }
};

export const saveStoredAuthUser = (user: StoredAuthUser | null | undefined): void => {
  if (typeof window === 'undefined') return;

  if (!user) {
    try {
      window.localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    } catch {
      // Ignore storage issues
    }
    return;
  }

  try {
    window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
  } catch {
    // Ignore storage issues
  }
};

export const getStoredAuthUser = (): StoredAuthUser | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAuthUser | null;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

export const isStoredAdminUser = (): boolean => {
  const role = getStoredAuthUser()?.role;
  return typeof role === 'string' && role === 'admin';
};

export const clearStoredAuthUser = (): void => {
  saveStoredAuthUser(null);
};

export const clearAllStoredAuthState = (): void => {
  clearStoredAuthSession();
  clearStoredAuthUser();
};
